// MobilePendingApprovalsView.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import StaticPopup from "components/StaticPopup";
import Loading from "components/Loading";
import ReactHtmlParser from 'react-html-parser';
import { deleteRfq, restoreRfq, trashedRfq, ApproveRfq, rejectRfqApproval } from "actions/rfqActions";
import { useSnackbar } from "notistack";
import MobileManagerNavigation from "components/MobileManagerNavigation";


const MobilePendingApprovalsView = ({
    rfqsData,
    user,
    entityId,
    teamUserIdsInString,
    startDate,
    endDate,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { enqueueSnackbar } = useSnackbar();

    const [openActionModal, setOpenActionModal] = useState(false);
    const [selectedRfq, setSelectedRfq] = useState(null);
    const [actionType, setActionType] = useState('');
    const [comments, setComments] = useState('');
    const [loadingAction, setLoadingAction] = useState(false);
    const [openVendorsModal, setOpenVendorsModal] = useState(false);
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [approvalRejectRemark, setApprovalRejectRemark] = useState('');
    const [rejectError, setRejectError] = useState('');


    // Filter only pending approvals
    const pendingApprovals = (rfqsData?.rfqs ?? []).filter((rfq) => {
        if (rfq.status !== "PENDING APPROVAL") return false;

        const approvalDetails = rfq.approvalDetails ?? [];
        const userApprovalLevel = approvalDetails.find(
            (approval) =>
                approval.approvers?.some((a) => a.userId === user?.userId)
        );

        if (!userApprovalLevel) return false;
        if (userApprovalLevel.status !== "PENDING") return false;

        const userLevelNumber = userApprovalLevel.level;
        if (userLevelNumber === 1) return true;

        const previousLevelsApproved = approvalDetails
            .filter((approval) => approval.level < userLevelNumber)
            .every((approval) => approval.status === "APPROVED");

        return previousLevelsApproved;
    });

    const handleOpenRfq = (rfqId) => {
        navigate(`/rfqs/${rfqId}`);
    };



    const handleOpenActionModal = (rfq, type) => {
        setSelectedRfq(rfq);
        setActionType(type);
        setComments('');
        setOpenActionModal(true);
    };

    const handleRejectApproval = async () => {
        if (!approvalRejectRemark?.trim()) {
            setRejectError('Remark is required');
            return;
        }

        const payload = {
            rfqId: selectedRfq.rfqId,
            entityId,
            approvalRejectRemark,
            userId: user?.userId
        };

        try {
            // await dispatch(rejectRfqApproval({ payload }));

            // enqueueSnackbar(
            //     'Approval rejected & RFQ sent for rework',
            //     { variant: 'success', autoHideDuration: 3000 }
            // );

            setOpenActionModal(false);
            setSelectedRfq(null);
            setApprovalRejectRemark('');
            setRejectError('');
        } catch (error) {
            enqueueSnackbar(
                error?.response?.data?.message || 'Failed to reject RFQ',
                { variant: 'error', autoHideDuration: 3000 }
            );
        }
    };

    const handleApproveApproval = () => {
        if (!selectedRfq) return;
        navigate(`/rfqs/${selectedRfq.rfqId}/selected-rates`);
    };

    const handleOpenSummary = (rfq) => {
        if (!rfq?.rfqId) return;

        navigate(`/rfqs/${rfq.rfqId}/selected-rates`, {
            state: { initialMobileTab: "negotiation" },
        });
    };

    const getSelectedVendorsCount = (suppliers) => {
        return suppliers?.filter(s => s.isAwarded)?.length || 0;
    };

    const getVendorPersonName = (supplier) => {
        if (!supplier) return '—';

        // 1️ Contact person (preferred)
        if (Array.isArray(supplier.contactDetails)) {
            const contactWithName = supplier.contactDetails.find(
                c => c?.name && c.name.trim()
            );
            if (contactWithName) return contactWithName.name;
        }

        // 2️ Added by (fallback – still person name)
        if (supplier.addedBy?.name) {
            return supplier.addedBy.name;
        }

        // 3️ Absolute fallback
        return '—';
    };

    const hasAnySelectedItem = (supplier) => {
        const quotationSet =
            supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];

        if (!quotationSet) return false;

        // MATERIAL RFQ
        if (Array.isArray(quotationSet.materialDetails)) {
            return quotationSet.materialDetails.some(m => m.isSelected);
        }

        // LABOUR RFQ
        if (Array.isArray(quotationSet.labourDetails)) {
            return quotationSet.labourDetails.some(l => l.isSelected);
        }

        // CONTRACTOR RFQ (sections → items)
        if (Array.isArray(quotationSet.sections)) {
            return quotationSet.sections.some(section =>
                Array.isArray(section.items) &&
                section.items.some(item => item.isSelected)
            );
        }

        return false;
    };


    const getSelectedVendorNames = (rfq) => {
        if (!rfq?.suppliers?.length) return [];

        return rfq.suppliers
            .filter(supplier =>
                supplier.isAwarded || hasAnySelectedItem(supplier)
            )
            .map(supplier => supplier.businessName)
            .filter(Boolean);
    };


    const getPreviousApprovers = (rfq) => {
        const comments = rfq?.commentsToApprover || [];
        if (!comments.length) return [];

        const map = new Map();

        comments.forEach(c => {
            const name =
                c.addedByName ||
                c.addedBy ||
                'Approver';

            const date = c.date;
            if (!date) return;

            const key = `${name}-${date}`;
            if (!map.has(key)) {
                map.set(key, { name, date });
            }
        });

        return Array.from(map.values());
    };

    const formatDate = (date) => {
        if (!date) return '';

        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();

        return `${day}/${month}/${year}`;
    };


    const monthNames = [
        "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12",
    ];

    if (rfqsData?.loading) {
        return (
            <div className="flex flex-col justify-center items-center h-[100vh] bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF]">
                <div className="animate-spin rounded-full border-2 mb-2 border-t-2 border-orange-500 border-t-gray-200 h-8 w-8"></div>
                <p className='text-sm font-medium'>Fetching Approvals...</p>
            </div>
        );
    }

    // Simple quotation value (all vendor quotations)
    const calculateTotalQuotationValue = (suppliers) => {
        if (!suppliers || suppliers.length === 0) return 0;

        const total = suppliers.reduce((sum, supplier) => {
            const quotationValue = supplier.vendorQuotations?.grandTotalWithoutGst || 0;
            return sum + quotationValue;
        }, 0);

        return total;
    };

    const calculateTotalSelectedQuotationValue = (rfq) => {
        if (!rfq.suppliers || rfq.suppliers.length === 0) return 0;

        let total = 0;

        // If RFQ is CLOSED → use awarded amount
        if (rfq.status === "CLOSED") {
            const awardedSuppliers = rfq.suppliers?.filter(s => s.isAwarded) || [];
            return awardedSuppliers.reduce((sum, supplier) => {
                const amount = parseFloat(supplier.closingDetails?.[0]?.amount) || 0;
                return sum + amount;
            }, 0);
        }

        // Pending approval → selected materials + extra charges
        rfq.suppliers.forEach(supplier => {
            const quotationSet =
                supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

            if (!quotationSet) return;

            // Selected materials
            if (quotationSet.materialDetails) {
                const selectedMaterials =
                    quotationSet.materialDetails.filter(m => m.isSelected);

                const materialTotal = selectedMaterials.reduce((sum, material) => {
                    const basePrice = material.basePrice || 0;
                    const discount = material.discount || 0;
                    const qty = material.quantity || 0;
                    const transport = material.transportCost || 0;

                    const discounted =
                        basePrice - (basePrice * discount) / 100;

                    return sum + (discounted * qty) + transport;
                }, 0);

                total += materialTotal;
            }

            // Extra charges
            if (quotationSet.selectedOthersTotalWithoutGst) {
                total += quotationSet.selectedOthersTotalWithoutGst;
            }
        });

        return total;
    };

    const getSelectedQuotationTotal = (rfq) => {
        if (!rfq?.suppliers?.length) return 0;

        // Prefer awarded vendor (CLOSED RFQ)
        if (rfq.status === "CLOSED") {
            const awarded = rfq.suppliers.find(s => s.isAwarded);
            return awarded?.closingDetails?.[0]?.amount || 0;
        }

        // Pending approval → L1 / selected vendor total
        const selectedSupplier =
            rfq.suppliers
                .filter(s => s.quotationReceived)
                .sort((a, b) => {
                    const ra = parseInt(a.vendorQuotations?.rank?.slice(1) || 99);
                    const rb = parseInt(b.vendorQuotations?.rank?.slice(1) || 99);
                    return ra - rb;
                })[0];

        return selectedSupplier?.vendorQuotations?.grandTotalWithoutGst || 0;
    };

    const getTotalQuotationValue = (rfq) => {
        if (!rfq?.suppliers?.length) return 0;

        return rfq.suppliers
            .filter(supplier => supplier.isAwarded)
            .reduce((sum, supplier) => {
                const amount = Number(
                    supplier?.closingDetails?.[0]?.amount || 0
                );
                return sum + amount;
            }, 0);
    };

    const getRfqCreatedDate = (rfq) => {
        const date =
            rfq?.createdAt ||
            rfq?.createdOn ||
            rfq?.createdDate;

        if (!date) return null;

        return formatDate(date); //DD/MM/YYYY
    };

    const formatTimeIST = (date, time) => {
        if (!time) return '';

        // combine date + time to avoid timezone bugs
        const isoString = date
            ? `${date}T${time}`
            : `1970-01-01T${time}`;

        const d = new Date(isoString);

        return d.toLocaleTimeString('en-IN', {
            timeZone: 'Asia/Kolkata',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };


    return (
        <div className="md:hidden min-h-screen bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF]">
            {/* Mobile Header */}
            <div className="fixed inset-x-0 top-0 z-[1000] flex min-h-[56px] items-center border-b border-[#EEE6DD] bg-white px-3 py-2 shadow-md">
                <MobileManagerNavigation className="w-full" />
            </div>
            <div className="h-[56px] shrink-0" aria-hidden="true" />

            {/* Mobile Content */}
            <div className="py-2">
                {pendingApprovals.length === 0 ? (
                    <div className=" flex justify-center items-center bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF]">
                        <div className="flex flex-col items-center">
                            <img
                                src="https://storagereponeevaydevcdn.blob.core.windows.net/business/Search_image.png"
                                alt="No approvals"
                                className="h-[116px] w-[122px] mb-6"
                            />
                            <p className="text-md font-semibold text-[#3C3C3C] text-center">
                                No pending approvals found.
                                <br /> All RFQs have been processed.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="px-4 overflow-y-auto bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF]">
                        {pendingApprovals.map((rfq, index) => (
                            <div
                                key={rfq.rfqId}
                                className="bg-white p-4 mb-3 space-y-1 shadow-md rounded-md border border-gray-200"
                            >
                                {/* RFQ Subject - Clickable */}
                                <p className="text-sm break-words w-full cursor-pointer"
                                    onClick={() => navigate(`/rfqs/${rfq.rfqId}/selected-rates`)}
                                >
                                    <span className="text-gray-600">RFQ Subject: </span>
                                    <span className="text-blue-500 font-medium">{rfq?.name || "N/A"}</span>
                                </p>

                                {/* RFQ ID */}
                                <p className="text-sm">
                                    <span className="text-gray-600">RFQ No.: </span>
                                    <span className="font-medium">{rfq?.rfqId || "N/A"}</span>
                                </p>

                                {/* Sender */}
                                <p className="text-sm">
                                    <span className="text-gray-600">Sender: </span>
                                    <span className="font-medium">
                                        {rfq.requesterName || "N/A"}
                                        {getRfqCreatedDate(rfq) && (
                                            <span>
                                                {' '}| {getRfqCreatedDate(rfq)}
                                            </span>
                                        )}
                                    </span>
                                </p>


                                {/* Project */}
                                <p className="text-sm">
                                    <span className="text-gray-600">Project: </span>
                                    <span className="font-medium">
                                        {rfq.projectName?.includes("||")
                                            ? `${rfq.projectName.split("||")[0].trim()}`
                                            : (rfq.projectName || 'N/A')}
                                    </span>
                                </p>

                                <div className="text-sm">
                                    <span className="text-gray-600 block">Previous Approver(s):</span>
                                    {getPreviousApprovers(rfq).length > 0 ? (
                                        <div className="space-y-0.5">
                                            {getPreviousApprovers(rfq).map((a, i) => (
                                                <div key={i} className="font-medium">
                                                    {a.name} | {formatDate(a.date)}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="italic text-gray-400">None</span>
                                    )}
                                </div>

                                {/* <div className="flex items-start">
                                    <span className="text-sm text-gray-500 flex-shrink-0">
                                        Total Quotation Value:
                                    </span>
                                    <span className="text-sm font-semibold pl-1">
                                        Rs. {new Intl.NumberFormat("en-IN").format(
                                            getTotalQuotationValue(rfq)
                                        )}
                                    </span>
                                </div> */}

                                {/* Selected Vendors Count */}
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-500">
                                        Selected Vendor(s):
                                    </span>

                                    <div className="text-sm font-medium">
                                        {getSelectedVendorNames(rfq).length > 0 ? (
                                            getSelectedVendorNames(rfq).map((name, i) => (
                                                <div key={i}>{name}</div>
                                            ))
                                        ) : (
                                            <span className="italic text-gray-400">
                                                No vendor selected
                                            </span>
                                        )}
                                    </div>
                                </div>


                                {/* Comments */}
                                <div className="text-sm">
                                    <span className=" font-semibold block">Comments:</span>

                                    {rfq?.commentsToApprover?.length > 0 ? (
                                        <div className="space-y-1">
                                            {rfq.commentsToApprover.map((item, index) => (
                                                <div key={index}>
                                                    {/* Comment content */}
                                                    <div className="text-gray-800 italic text-sm">
                                                        {ReactHtmlParser(item.comment || "")}
                                                    </div>

                                                    {/* Meta info */}
                                                    <div className="text-xs text-gray-500">
                                                        <span>
                                                            — {item.addedByName || "Unknown"}
                                                        </span>
                                                        {item.time && (
                                                            <span>
                                                                {" "}At {formatTimeIST(item.date, item.time)}
                                                            </span>
                                                        )}
                                                        {item.date && (
                                                            <span>
                                                                {" "} {formatDate(item.date)}
                                                            </span>
                                                        )}
                                                       

                                                    </div>

                                                    {/* Separator */}
                                                    {index !== rfq.commentsToApprover.length - 1 && (
                                                        <hr className="my-2 border-gray-200" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-500 italic">No comments</span>
                                    )}
                                </div>

                                {/* Created Date */}
                                {/* <p className="text-xs text-gray-500">
                                    Created: {rfq.createdAt
                                        ? `${new Date(rfq.createdAt).getDate()}/${monthNames[new Date(rfq.createdAt).getMonth()]}/${new Date(rfq.createdAt).getFullYear()}`
                                        : "N/A"}
                                </p> */}

                                {/* Created Date and Action Buttons */}
                                <div className="flex justify-end items-center gap-2 w-full pt-3 ">
                                    {/* Action Buttons */}
                                    {/* In MobilePendingApprovalsView.jsx - Update the View Details button */}
                                    {/* <button
                                        type="button"
                                        onClick={() => handleOpenSummary(rfq)}
                                        className="py-1 px-3 text-xs border border-[#004491] text-[#004491] rounded-sm flex items-center"
                                    >
                                        View Details
                                    </button> */}
                                    <button
                                        onClick={() => navigate(`/rfqs/${rfq.rfqId}/selected-rates`)}
                                        className="py-1 px-3 text-xs border border-gray-800 rounded-sm flex items-center"
                                    >
                                        View Quotations
                                    </button>
                                    {/* <div className="flex gap-2">
                                        <button
                                            onClick={() => handleOpenActionModal(rfq, 'reject')}
                                            className="px-3 py-1 text-xs border border-gray-800 rounded-sm"
                                        >
                                            Reject
                                        </button>

                                        <button
                                            onClick={() => handleOpenActionModal(rfq, 'approve')}
                                            className="px-3 py-1 text-xs text-white border border-gray-800 bg-gray-800 rounded-sm"
                                        >
                                            Approve
                                        </button>

                                    </div> */}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Approve/Reject Action Modal */}
            <StaticPopup
                openModal={openActionModal}
                handleClose={() => setOpenActionModal(false)}
                header={actionType === 'approve' ? 'Approve RFQ' : 'Reject RFQ'}
            >
                <div className="space-y-4">
                    <p className="text-sm">
                        You are about to <span className="font-semibold">{actionType}</span> RFQ #{selectedRfq?.rfqId}
                    </p>

                    {actionType === 'reject' && (
                        <>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Reject Remark *
                            </label>

                            <textarea
                                value={approvalRejectRemark}
                                onChange={(e) => {
                                    setApprovalRejectRemark(e.target.value);
                                    setRejectError('');
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                rows="3"
                                placeholder="Enter rejection remark"
                            />

                            {rejectError && (
                                <p className="text-xs text-red-600 mt-1">
                                    {rejectError}
                                </p>
                            )}
                        </>
                    )}

                    <div className="flex gap-4 mt-4">
                        <button
                            className="flex-1 p-2 border rounded-lg font-semibold hover:bg-slate-100"
                            onClick={() => setOpenActionModal(false)}
                        >
                            Cancel
                        </button>

                        <button
                            className={`flex-1 p-2 px-4 border rounded-lg text-white font-semibold ${actionType === 'approve'
                                ? 'border border-gray-800 bg-gray-800'
                                : 'bg-red-600'
                                }`}
                            onClick={
                                actionType === 'approve'
                                    ? handleApproveApproval
                                    : handleRejectApproval
                            }
                        >
                            {actionType === 'approve' ? 'Approve' : 'Reject RFQ'}
                        </button>


                    </div>
                </div>
            </StaticPopup>


            {/* Selected Vendors Modal */}
            <div className={`fixed inset-0 z-50 overflow-y-auto ${openVendorsModal ? 'block' : 'hidden'}`}>
                <div className="flex items-center justify-center px-4 pt-4 text-center sm:block sm:p-0">
                    {/* Background overlay */}
                    <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setOpenVendorsModal(false)}></div>

                    {/* Modal panel */}
                    <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Selected Vendors</h3>

                            <div className="space-y-3 overflow-y-auto">
                                {selectedVendors.length === 0 ? (
                                    <p className="text-sm text-gray-400 text-center py-4">No vendors selected</p>
                                ) : (
                                    selectedVendors.map((vendor, index) => (
                                        <div
                                            key={`${vendor._id}-${index}`}
                                            className="border-b border-gray-200 pb-3 last:border-0"
                                        >
                                            <p className="font-medium text-sm mb-1">{vendor.businessName}</p>
                                            <div className="text-xs text-gray-600 space-y-1">
                                                <p>Contact: {vendor.contactDetails?.[0]?.mobile || 'N/A'}</p>
                                                <p>Email: {vendor.contactDetails?.[0]?.email || 'N/A'}</p>
                                                <p className="font-semibold">
                                                    Quotation: Rs. {vendor.quotationValue
                                                        ? new Intl.NumberFormat("en-IN").format(
                                                            parseFloat(vendor.quotationValue).toFixed(2)
                                                        )
                                                        : 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                type="button"
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                                onClick={() => setOpenVendorsModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MobilePendingApprovalsView;
